'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var EventEmitter = _interopDefault(require('eventemitter3'));
var store$1 = _interopDefault(require('store2'));
var uuidv4 = _interopDefault(require('uuid/v4'));
var merge = _interopDefault(require('lodash/merge'));
var ansicolor = require('ansicolor');
var ansicolor__default = _interopDefault(ansicolor);
var hashIndex = _interopDefault(require('hash-index'));
var omit = _interopDefault(require('lodash/omit'));
var pickBy = _interopDefault(require('lodash/pickBy'));
var dateFns = require('date-fns');
var isString = _interopDefault(require('lodash/isString'));
var isInteger = _interopDefault(require('lodash/isInteger'));
var isNumber = _interopDefault(require('lodash/isNumber'));
var isDate = _interopDefault(require('lodash/isDate'));
var isBoolean = _interopDefault(require('lodash/isBoolean'));
var isArray = _interopDefault(require('lodash/isArray'));
var isPlainObject = _interopDefault(require('lodash/isPlainObject'));

class EventTracker {
    /**
     * This method allows for some limited runtime "reflection" of the event tracking methods.
     *   Generic trackers that do not need to manually implement every event tracking method
     *   can instead programmatically implement every method using this helper.
     *
     * See the `EmittingTracker` as an example.
     *
     * Note: Other "generic" trackers should extend the `EmittingTracker` rather than
     *   use this helper directly.
     */
    static getEventTrackingMethodNames() {
        return Object.getOwnPropertyNames(EventTracker.prototype).filter(methodName => {
            return methodName !== 'constructor';
        });
    }
    // Navigation
    pageViewed() { }
    // Experimentation
    experimentViewed(_event) { }
    // User Acquisition
    userRegistered(_event) { }
    userProfileCollectionModalTriggered(_event) { }
    userProfileCollectionModalSubmitted(_event) { }
    fullPageTakeoverModalSubmitted(_event) { }
    fullPageTakeoverModalDismissed(_event) { }
    consumerReferralLinkShareButtonClicked(_event) { }
    referralPageEntryPointViewed(_event) { }
    consumerVisitedFromReferralLink(_event) { }
    referralPageViewed(_event) { }
    engagementChannelViewed(_event) { }
    engagementChannelDismissed(_event) { }
    engagementChannelSubmitted(_event) { }
    // Paths
    pathFlowCreated(_event) { }
    pathQuestionSubmitted(_event) { }
    pathQuestionViewed(_event) { }
    pathPageViewed(_event) { }
    pathPageSubmitted(_event) { }
    pathExited(_event) { }
    // Requests
    requestSubmitted(_event) { }
    // Search
    productsSearched(_event) { }
    productsSearchResultsViewed(_event) { }
    // Sellables
    productViewed(_event) { }
    productViewEnded(_event) { }
    productAdded(_event) { }
    productRemoved(_event) { }
    productAddClicked(_event) { }
    productClicked(_event) { }
    productRemovedFromWishlist(_event) { }
    productAddedToWishlist(_event) { }
    productRated(_event) { }
    priceComparisonsViewed(_event) { }
    priceComparisonsExpanded(_event) { }
    priceComparisonsCollapsed(_event) { }
    priceComparisonsChatWithExpert(_event) { }
    // Curated lists
    curatedListViewed(_event) { }
    curatedItemRemoved(_event) { }
    curatedListExpertNoteHelpful(_event) { }
    curatedListExpertNoteNotHelpful(_event) { }
    viewCurationLtv(_event) { }
    // Orders
    guestCheckoutStarted(_event) { }
    checkoutStarted(_event) { }
    checkoutStepCompleted(_event) { }
    checkoutStepViewed(_event) { }
    paymentCancelled(_event) { }
    paymentFailed(_event) { }
    threeDSecureNotSupported(_event) { }
    orderCompleted(_event) { }
    // Feedback
    feedbackSubmitted(_event) { }
    // TODO: Organize these events!
    expertRatingFlowStarted(_event) { }
    expertRatingFlowCompleted(_event) { }
    viewExpertProfileClicked(_event) { }
    engagementModalSubmitted() { }
    engagementModalDismissed() { }
    landingPageSubmitted(_event) { }
    landingPageViewed(_event) { }
    expertCuratedItemVariationSelected(_event) { }
    cartAccepted(_event) { }
    noConversationsRequestModalTriggered() { }
    itineraryPageViewed(_event) { }
    paymentInfoEntered(_event) { }
    expertApplicationSubmitted() { }
    expertNoteHelpful(_event) { }
    expertNoteNotHelpful(_event) { }
    clientSessionReset(_event) { }
    shopperApprovedWidgetClicked(_event) { }
    userLoggedIn(_event) { }
    curatedStatusBarViewCurationClicked() { }
    desktopFixedBarClicked() { }
    mobileFixedBarClicked() { }
    expertCalloutChatClicked() { }
    pushNotificationsDeniedOnLoad() { }
    pushNotificationsAllowedOnLoad() { }
    pushNotificationsAllowed() { }
    pushNotificationsDenied() { }
    expertCuratedOfferCtaClicked() { }
    phoneVerificationCodeIssued() { }
    phoneVerificationCodeConfirmed() { }
    phoneVerificationCodeErrored() { }
    phoneVerificationContactUpdated() { }
    expertProfileConversationStarted() { }
    expertProfileSellableClick() { }
    expertProfileCategoryTableChanged() { }
    chartersRedirectVisited() { }
    contactCollectionCardSubmitted() { }
    brandCategoryModalDismissed() { }
    smsModalDismissed() { }
    topicPageHelpful(_event) { }
    topicPageNotHelpful(_event) { }
}
class Tracker extends EventTracker {
    constructor(client) {
        super();
        this._client = client;
    }
    /**
     * Associate a tracked user with a persistent User ID.
     *
     * @param trackingId
     */
    identify(_trackingId) { }
    /**
     * Add traits (PII) to the tracked user.
     *
     * @param traits
     */
    addTraits(_traits) { }
    /**
     * Set the department of the tracked user.
     *
     * @param departmentSlug
     */
    setDepartment(_departmentSlug) { }
    /**
     * Set the current page key.
     *
     * @param pageKey
     */
    setPageKey(_pageKey) { }
    /**
     * Mark the start of a new session and associate it with marketing/acquisition data
     *
     * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
     *
     * @param attributes
     */
    touch(_attributes) { }
    /**
     * Enable session recording. This only applies for the FullStory tracker.
     */
    record() { }
    /**
     * Disassociate the tracked user from the previously identified user ID, and
     *   clear any other user-specific state/traits.
     */
    reset() { }
    /**
     * Disable tracking entirely.
     *
     * Note: individual trackers do not need to implement this method as tracking is
     *   disabled at the client level. This is exists solely for the ConsoleTracker.
     */
    disable() { }
    /**
     * Enable tracking.
     *
     * Note: individual trackers do not need to implement this method as tracking is
     *   disabled at the client level. This is exists solely for the ConsoleTracker.
     */
    enable() { }
}

/**
 * This Tracker that emits an EventEmitter event every time a tracking event is fired.
 */
class EmittingTracker extends Tracker {
    constructor(client) {
        super(client);
        this.emitter = new EventEmitter();
        EventTracker.getEventTrackingMethodNames().forEach(methodName => {
            this[methodName] = (eventData, ...args) => {
                this.emitter.emit('event', methodName, eventData, ...args);
            };
        });
    }
    onEvent(listenerFn) {
        this.emitter.on('event', (eventName, eventData, ...args) => listenerFn(eventName, eventData, ...args));
    }
}

var store = store$1.namespace('tracking');

class Id {
    constructor(type, underlyingId) {
        /**
         * Get the string respresentation of this ID.
         */
        this.toString = this.get.bind(this);
        this._type = type;
        this.set(underlyingId || this.load() || this.mint());
    }
    /**
     * Set the string representation of this ID.
     *
     * Note: IDs are double-written to memory and localStorage for performance. If we
     *   didn't keep the ID in memory we'd incur the non-trivial cost of frequent
     *   localStorage lookups.
     *
     * See: https://stackoverflow.com/questions/8074218/speed-cost-of-localstorage
     */
    set(id) {
        this._id = id;
        store.set(this._type, this._id);
    }
    /**
     * Get the string representation of this ID.
     *
     * This is an alias of `toString`, but intended to be used internally.
     */
    get() {
        return this._id;
    }
    /**
     * Generate a new universally-unique identifier.
     */
    mint() {
        return uuidv4();
    }
    /**
     * Fetch the identifier from localStorage. Used during initialization to ensure
     *   identifiers are persisted across page reloads.
     */
    load() {
        return store.get(this._type);
    }
    /**
     * Reset the identifier.
     *
     *  - If no `id` is provided, a new identifier is minted.
     *  - If an `id` is provided, this method is identical to `set`.
     */
    reset(id = this.mint()) {
        this.set(id);
    }
}

/**
 * A unique identifier for a given browser. This ID is never reset (unless the user
 *   manually clears their browser storage) and is not user-specific.
 */
class BrowserId extends Id {
    constructor() {
        super('browserId');
    }
}

/**
 * A client-side Session ID is minted when a user first visits our website. It is persisted
 *   in a never-expiring cookie, and is only explicitly reset when a user logs out.
 *
 * Note: This is a _client side_ session identifier. This is totally unrelated to our backend
 *   `SessionId` type!
 *
 * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
 */
class SessionId extends Id {
    constructor(underlyingId) {
        super('sessionId', underlyingId);
    }
}

/**
 * A "Tracking ID" is minted for every guest user who visits the site. If that user registers,
 *   the tracking ID is persisted and used for all future visits to the site by that user (once
 *   they login). This association is made using the `identify` method.
 */
class TrackingId extends Id {
    constructor() {
        super('trackingId');
    }
}

/**
 * Model a user's clicks/touchpoints with our application over time.
 *
 * WARNING:
 * -------
 *
 * These client-side models are not intended to be used a system of record for marketing attribution.
 *   Touchpoints ae persisted in localStorage, meaning this model is browser-specific. Due to the
 *   inherent multi-device nature of our product, a single-browser view of attribution is generally
 *   insufficient for any kind of complex analysis.
 *
 * The client-side attribution modelling exists primarily for backwards compatibility: as of December 2019,
 *   we send attribution data with every Segment event. This event-level attribution is used for some
 *   analytics queries, and a snapshot is persisted to the backend when a user registers (as "User Source"
 *   data, which is used for campaign management).
 */
class AttributionModel {
    constructor() {
        const [firstTouch, lastTouch] = this.load();
        this.setFirstTouch(firstTouch);
        this.setLastTouch(lastTouch);
    }
    /**
     * Update the first touch in memory & local storage.
     */
    setFirstTouch(touch) {
        if (!touch) {
            delete this._firstTouch;
            store.remove('firstTouch');
        }
        else {
            this._firstTouch = touch;
            store.set('firstTouch', this._firstTouch);
        }
    }
    /**
     * Update the last touch in memory & local storage.
     */
    setLastTouch(touch) {
        if (!touch) {
            delete this._lastTouch;
            store.remove('lastTouch');
        }
        else {
            this._lastTouch = touch;
            store.set('lastTouch', this._lastTouch);
        }
    }
    /**
     * Fetch the touchpoints from localStorage
     */
    load() {
        const firstTouch = store.get('firstTouch');
        const lastTouch = store.get('lastTouch');
        // Store2 can't automatically deserialize Dates, so we do it explicitly
        if (firstTouch)
            firstTouch.timestamp = new Date(firstTouch.timestamp);
        if (lastTouch)
            lastTouch.timestamp = new Date(lastTouch.timestamp);
        return [firstTouch, lastTouch];
    }
    /**
     * Erase the recorded touchpoints
     */
    reset() {
        this.setFirstTouch(null);
        this.setLastTouch(null);
    }
    /**
     * Record a new touchpoint. Backdating is supported.
     */
    touch(attributes, timestamp = new Date()) {
        const touchPoint = {
            timestamp,
            attributes
        };
        if (!this._firstTouch) {
            this.setFirstTouch(touchPoint);
        }
        this.setLastTouch(touchPoint);
    }
    /**
     * Compute first-touch attribution for the recorded touchpoints
     */
    get firstTouch() {
        return this._firstTouch;
    }
    /**
     * Compute last-touch attribution for the recorded touchpoints
     */
    get lastTouch() {
        return this._lastTouch;
    }
}

class Traits {
    constructor() {
        this.set(this.load() || {});
    }
    load() {
        return store.get('traits') || {};
    }
    set(traits) {
        this._traits = traits;
        store.set('traits', traits);
    }
    add(traits) {
        const updatedTraits = merge(this._traits, traits);
        this.set(updatedTraits);
    }
    get() {
        return this._traits;
    }
    reset() {
        this.set({});
    }
}

class Department {
    constructor() {
        const existingDepartmentSlug = this.load();
        if (existingDepartmentSlug) {
            this.set(existingDepartmentSlug);
        }
    }
    load() {
        return store.get('department') || undefined;
    }
    set(departmentSlug) {
        this._department = departmentSlug;
        store.set('department', departmentSlug);
    }
    get() {
        return this._department;
    }
    reset() {
        this._department = undefined;
        store.remove('department');
    }
}

class Analytics {
    constructor(options) {
        this._identified = false;
        this._disabled = false;
        // Initialize identifiers
        this._sessionId = new SessionId(options.sessionId);
        this._browserId = new BrowserId();
        this._trackingId = new TrackingId();
        // Initialize user traits
        this._traits = new Traits();
        // Initialize the user's department
        this._department = new Department();
        // Initialize attribution models
        this._attribution = new AttributionModel();
        // Initialize the trackers
        this._trackers = options.trackers.map(trackerFactory => {
            return trackerFactory(this);
        });
        // Initialize this client's public tracker API, which notifies all internal trackers
        this.events = new EmittingTracker(this);
        this.events.onEvent((eventName, eventData, ...args) => {
            this.track(eventName, eventData, ...args);
        });
    }
    get browserId() {
        return this._browserId.toString();
    }
    get sessionId() {
        return this._sessionId.toString();
    }
    get trackingId() {
        return this._trackingId.toString();
    }
    get firstTouch() {
        return this._attribution.firstTouch;
    }
    get lastTouch() {
        return this._attribution.lastTouch;
    }
    get identified() {
        return this._identified;
    }
    track(eventName, eventData, ...args) {
        if (this._disabled) {
            return;
        }
        this._trackers.forEach(tracker => {
            // @ts-ignore This is safe so long as the emitting tracker emits compatible
            //   event names and data
            tracker[eventName].call(tracker, eventData, ...args);
        });
    }
    /**
     * Mark the start of a new attribution session and associate it with acquisition data.
     *
     * This method attempts to achieve a level of idempotency. This method is a no-op if either
     *   of the following are true:
     *
     *  - The window was loaded by reloading/refreshing the page.
     *  - The window's referrer has the same host as the active URL (i.e. the user is not coming
     *      from an external host).
     *
     * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
     *
     * @param attributes Tracking.TouchPointAttributes
     */
    touch(attributes) {
        if (this._disabled) {
            return;
        }
        // TODO: This method was originally designed to be purely functional, but now inspects the
        //   document state (window.perfomance.navigation, window.document.referrer, and
        //   window.location.href) in order to decide which touches to ignore.
        //
        // We should either update this method to accept that context as additional arguments, or
        //   accept no arguments and instead derive all the tracking parameters from the window
        //   directly.
        // N.B.: The `window.performance.navigation` API is deprecated in favor of the Navigation
        //   Timing Level 2 specification, but the newer specification has poor support currently.
        //   Notably, Safari is missing support for the newer API. For now, this works well.
        const isPageRefresh = window.performance &&
            window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
        // N.B.: `initAppUrl` === `window.location.href` and `referrerUrl` === `window.document.referrer`
        //   (assuming the attributes are populated using the utils exported by this library)
        const isReferredBySameHost = attributes.initAppUrl &&
            attributes.referrerUrl &&
            new URL(attributes.initAppUrl).host === new URL(attributes.referrerUrl).host;
        // Ignore invalid touch points
        if (isPageRefresh || isReferredBySameHost) {
            return;
        }
        this._attribution.touch(attributes);
        this._trackers.forEach(tracker => {
            tracker.touch(attributes);
        });
    }
    /**
     * Set the TrackingId for the tracked user.
     *
     * This method is idempotent: subsequent consecutive calls with the same ID are ignored.
     */
    identify(trackingId) {
        if (this._disabled) {
            return;
        }
        if (this._identified && this._trackingId.toString() === trackingId) {
            return;
        }
        this._identified = true;
        this._trackingId.set(trackingId);
        this._trackers.forEach(tracker => {
            tracker.identify(trackingId);
        });
    }
    /**
     * Add traits (PII) to the tracked user.
     *
     * Traits are overwritten in place.
     */
    addTraits(traits) {
        if (this._disabled) {
            return;
        }
        if (traits === undefined) {
            return this._traits;
        }
        this._traits.add(traits);
        // Allow individual trackers to reset their internal state
        this._trackers.forEach(tracker => {
            tracker.addTraits(traits);
        });
    }
    getTraits() {
        return this._traits.get();
    }
    /**
     * Set the department for the tracked user (by a department slug)
     *
     * Note: The definition of a user's department is loose. Consumers can shop across
     *   departments and be acquired from marketing associated with multiple departments.
     *   This field is only used to fire department-specific pixels for certain pixel
     *   implementations (e.g. Facebook) in order to build audiences for similar consumers.
     *   It should *not* be used for general department analytics, or attached to our own
     *   tracking events.
     */
    setDepartment(departmentSlug) {
        if (this._disabled) {
            return;
        }
        this._department.set(departmentSlug);
        this._trackers.forEach(tracker => {
            tracker.setDepartment(departmentSlug);
        });
    }
    /**
     * Get the current department for the tracked user.
     *
     * Note: It is generally a bad idea to rely on this field externally. See the note in
     *   `setDepartment` for more context.
     */
    getDepartment() {
        return this._department.get();
    }
    /**
     * Set the current  page key for future tracked events. This should be updated as the
     *   navigates around the site.
     *
     * Note: Unlike most other tracking context (e.g. the current department), the page key
     *   is stored in memory vs. localStorage. Events fired before setting the page key
     *   will *never* have a page key set, even if the user was previously tracked in the
     *   same browser.
     */
    setPageKey(pageKey) {
        if (this._disabled) {
            return;
        }
        this._pageKey = pageKey;
        this._trackers.forEach(tracker => {
            tracker.setPageKey(pageKey);
        });
    }
    /**
     * Get the current page key.
     */
    getPageKey() {
        return this._pageKey;
    }
    /**
     * Enable session recording. This only applies for the FullStory tracker.
     *
     * Note: All logged-in users will be recorded automatically, and a sample of logged-out
     *   users will be recorded as well. Use this method to explicitly enable session
     *   recording for "high value" users.
     */
    record() {
        if (this._disabled) {
            return;
        }
        this._trackers.forEach(tracker => {
            tracker.record();
        });
    }
    /**
     * Reset all state associated with the tracked user: identifiers, attribution data, and
     *   PII. The notable exception is the `deviceId` which is persisted forever.
     */
    reset(newSessionId) {
        if (this._disabled) {
            return;
        }
        // Reset the identified flag
        this._identified = false;
        // Reset the session and tracking IDs
        this._sessionId.reset(newSessionId);
        this._trackingId.reset();
        // Reset the local attribution model
        this._attribution.reset();
        // Reset the user traits
        this._traits.reset();
        // Reset the user's department
        this._department.reset();
        // Reset the page key
        this._pageKey = undefined;
        // Allow individual trackers to reset their internal state
        this._trackers.forEach(tracker => {
            tracker.reset();
        });
    }
    /**
     * Disable event tracking (e.g. when a user is being impersonated)
     */
    disable() {
        if (this._disabled) {
            return;
        }
        this._disabled = true;
        // Allow individual trackers to respond to being disabled
        this._trackers.forEach(tracker => {
            tracker.disable();
        });
    }
    /**
     * Enable event tracking
     */
    enable() {
        if (!this._disabled) {
            return;
        }
        this._disabled = false;
        this._trackers.forEach(tracker => {
            tracker.enable();
        });
    }
}

function displayNameForEvent(methodName) {
    return EVENT_METHOD_NAMES_TO_DISPLAY_NAMES[methodName];
}
const EVENT_METHOD_NAMES_TO_DISPLAY_NAMES = {
    pageViewed: 'Page Viewed',
    experimentViewed: 'Experiment Viewed',
    userRegistered: 'User Registered',
    userProfileCollectionModalTriggered: 'User Profile Collection Modal Triggered',
    userProfileCollectionModalSubmitted: 'User Profile Collection Modal Submitted',
    fullPageTakeoverModalSubmitted: 'Full Page Takeover Modal Submitted',
    fullPageTakeoverModalDismissed: 'Full Page Takeover Modal Dismissed',
    consumerVisitedFromReferralLink: 'Consumer Visited From Referral Link',
    consumerReferralLinkShareButtonClicked: 'Consumer Referral Link Share Button Clicked',
    referralPageEntryPointViewed: 'Referral Page Entry Point Viewed',
    referralPageViewed: 'Referral Page Viewed',
    pathFlowCreated: 'Path Flow Created',
    pathQuestionSubmitted: 'Path Question Submitted',
    pathQuestionViewed: 'Path Question Viewed',
    pathPageSubmitted: 'Path Page Submitted',
    pathPageViewed: 'Path Page Viewed',
    pathExited: 'Path Exited',
    requestSubmitted: 'Request Submitted',
    productsSearched: 'Products Searched',
    productsSearchResultsViewed: 'Products Search Results Viewed',
    productViewed: 'Product Viewed',
    productViewEnded: 'Product View Ended',
    productAdded: 'Product Added',
    productRemoved: 'Product Removed',
    productAddClicked: 'Product Add Clicked',
    productClicked: 'Product Clicked',
    productRemovedFromWishlist: 'Product Removed From Wishlist',
    productAddedToWishlist: 'Product Added To Wishlist',
    productRated: 'Product Rated',
    curatedListViewed: 'Curated List Viewed',
    curatedItemRemoved: 'Curated Item Removed',
    curatedListExpertNoteHelpful: 'Curated List Expert Note Helpful',
    curatedListExpertNoteNotHelpful: 'Curated List Expert Note Not Helpful',
    viewCurationLtv: 'View Curation LTV',
    checkoutStarted: 'Checkout Started',
    guestCheckoutStarted: 'Guest Checkout Started',
    checkoutStepCompleted: 'Checkout Step Completed',
    checkoutStepViewed: 'Checkout Step Viewed',
    paymentCancelled: 'Payment Cancelled',
    paymentFailed: 'Payment Failed',
    threeDSecureNotSupported: '3D Secure Not Supported',
    orderCompleted: 'Order Completed',
    expertRatingFlowStarted: 'Expert Rating Flow Started',
    expertRatingFlowCompleted: 'Expert Rating Flow Completed',
    viewExpertProfileClicked: 'View Expert Profile Clicked',
    engagementModalSubmitted: 'Engagement Modal Submitted',
    engagementModalDismissed: 'Engagement Modal Dismissed',
    landingPageSubmitted: 'Landing Page Submitted',
    landingPageViewed: 'Landing Page Viewed',
    expertCuratedItemVariationSelected: 'Expert Curated Item Variation Selected',
    cartAccepted: 'Cart Accepted',
    noConversationsRequestModalTriggered: 'No Conversations Request Modal Triggered',
    itineraryPageViewed: 'Itinerary Page Viewed',
    paymentInfoEntered: 'Payment Info Entered',
    expertApplicationSubmitted: 'Expert Application Submitted',
    expertNoteHelpful: 'Expert Note Helpful',
    expertNoteNotHelpful: 'Expert Note Not Helpful',
    clientSessionReset: 'Client Session Reset',
    shopperApprovedWidgetClicked: 'Shopper Approved Widget Clicked',
    userLoggedIn: 'User Logged In',
    curatedStatusBarViewCurationClicked: 'Curated Status Bar View Curation Clicked',
    desktopFixedBarClicked: 'Desktop Fixed Bar Clicked',
    mobileFixedBarClicked: 'Mobile Fixed Bar Clicked',
    expertCalloutChatClicked: 'Expert Callout Chat Clicked',
    pushNotificationsDeniedOnLoad: 'Push Notifications Denied On Load',
    pushNotificationsAllowedOnLoad: 'Push Notifications Allowed On Load',
    pushNotificationsAllowed: 'Push Notifications Allowed',
    pushNotificationsDenied: 'Push Notifications Denied',
    expertCuratedOfferCtaClicked: 'Expert Curated Offer CTA Clicked',
    phoneVerificationCodeIssued: 'Phone Verification Code Issued',
    phoneVerificationCodeConfirmed: 'Phone Verification Code Confirmed',
    phoneVerificationCodeErrored: 'Phone Verification Code Errored',
    phoneVerificationContactUpdated: 'Phone Verification Contact Updated',
    expertProfileConversationStarted: 'Expert Profile Conversation Started',
    expertProfileSellableClick: 'Expert Profile Sellable Click',
    expertProfileCategoryTableChanged: 'Expert Profile Category Table Changed',
    chartersRedirectVisited: 'Charters Redirect Visited',
    contactCollectionCardSubmitted: 'Contact Collection Card Submitted',
    brandCategoryModalDismissed: 'Brand Category Modal Dismissed',
    smsModalDismissed: 'SMS Modal Dismissed',
    topicPageHelpful: 'Topic Page Helpful',
    topicPageNotHelpful: 'Topic Page Not Helpful',
    feedbackSubmitted: 'Feedback Submitted',
    engagementChannelViewed: 'Engagement Channel Viewed',
    engagementChannelSubmitted: 'Engagement Channel Submitted',
    engagementChannelDismissed: 'Engagement Channel Dismissed',
    priceComparisonsViewed: 'Price Comparisons Viewed',
    priceComparisonsExpanded: 'Price Comparisons Expanded',
    priceComparisonsCollapsed: 'Price Comparisons Collapsed',
    priceComparisonsChatWithExpert: 'Price Comparisons Chat With Expert'
};

const COLORS = [
    ansicolor__default.bgLightRed.white.bright,
    ansicolor__default.bgLightGreen.white.bright,
    ansicolor__default.bgLightYellow.white.bright,
    ansicolor__default.bgLightBlue.white.bright,
    ansicolor__default.bgLightMagenta.white.bright,
    ansicolor__default.bgLightCyan.white.bright
];
/**
 * Returns an array of arguments to be passed to `console.log`, `console.group`, etc.
 */
function colorizeTextForWebConsole(color, text) {
    return ansicolor.parse(color(text)).asChromeConsoleLogArguments;
}
/**
 * Flatten the user traits for display in the console.
 */
function flattenUserTraits(traits) {
    const flattened = Object.assign(Object.assign({}, omit(traits, 'address')), (traits.address ? traits.address : {}));
    return pickBy(flattened);
}
/**
 * Log events to the browser console.
 *
 *  - Events are colorized according to the event name
 *  - Event payloads are displayed in a table format along with the event timestamp
 */
class ConsoleTracker extends EmittingTracker {
    constructor(client) {
        super(client);
        this.onEvent(this.log);
    }
    log(eventName, eventData, ...args) {
        const eventDisplayName = displayNameForEvent(eventName);
        const color = COLORS[hashIndex(eventName, COLORS.length)];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` ${eventDisplayName} `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        if (eventData) {
            console.table(eventData);
        }
        if (args.length) {
            console.log(...args);
        }
        console.groupEnd();
    }
    identify(trackingId) {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Identify `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        console.table({
            trackingId,
            sessionId: this._client.sessionId,
            browserId: this._client.browserId
        });
        console.groupEnd();
    }
    touch() {
        const color = COLORS[0];
        const firstTouch = this._client.firstTouch;
        const lastTouch = this._client.lastTouch;
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Touch `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `First Touch Attribution (${firstTouch.timestamp})`));
        console.table(firstTouch.attributes);
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `Last Touch Attribution (${lastTouch.timestamp})`));
        console.table(lastTouch.attributes);
        console.groupEnd();
    }
    addTraits(newTraits) {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Traits `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `New Traits`));
        console.table(flattenUserTraits(newTraits));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `All Traits`));
        console.table(flattenUserTraits(this._client.getTraits()));
        console.groupEnd();
    }
    setDepartment(departmentSlug) {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Department `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        console.table({
            department: departmentSlug
        });
        console.groupEnd();
    }
    setPageKey(pageKey) {
        const color = COLORS[0];
        if (pageKey) {
            console.groupCollapsed(...colorizeTextForWebConsole(color, ` Page Key `));
            console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
            console.table({
                pageKey: pageKey
            });
            console.groupEnd();
        }
        else {
            console.groupCollapsed(...colorizeTextForWebConsole(color, ` Page Key Unset `));
            console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
            console.groupEnd();
        }
    }
    reset() {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Reset `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        console.groupEnd();
    }
    disable() {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Tracking Disabled `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        console.groupEnd();
    }
    enable() {
        const color = COLORS[0];
        console.groupCollapsed(...colorizeTextForWebConsole(color, ` Tracking Enabled `));
        console.log(...colorizeTextForWebConsole(ansicolor__default.bright, `${new Date()}`));
        console.groupEnd();
    }
}

class SegmentTracker extends EmittingTracker {
    constructor(client) {
        super(client);
        if (!window.analytics) {
            throw new Error('The Segment snippet is missing, but must be installed to use the SegmentTracker.');
        }
        this.onEvent(this.handleEvent.bind(this));
    }
    /**
     * Resolve window.analytics.
     *
     * This getting exists solely to simplify typechecking. We can be certain
     *   `window.analytics` is defined since we check for it in the constructor.
     */
    get segmentAnalytics() {
        return window.analytics;
    }
    /**
     * Handle events as they are tracked.
     *
     * Segment treats page views as special events, so we forward events to
     *   either SegmentTracker.track or SegmentTracker.page as appropriate.
     */
    handleEvent(eventName, eventData) {
        const eventDisplayName = displayNameForEvent(eventName);
        if (eventName === 'pageViewed') {
            this.page(eventData);
        }
        else {
            this.track(eventDisplayName, eventData);
        }
    }
    /**
     * Associate a tracked user with a persistent User ID.
     *
     * @param trackingId
     */
    identify(trackingId) {
        const options = {
            anonymousId: this._client.sessionId,
            context: {
                traits: this.deriveAcquisitionAttributes(),
                pageKey: this._client.getPageKey()
            }
        };
        this.segmentAnalytics.ready(() => {
            this.segmentAnalytics.identify(trackingId, this._client.getTraits(), options);
        });
    }
    /**
     * Disassociate the tracked user from the previously identified user ID, and
     *   clear any other user-specific state/traits.
     */
    reset() {
        this.segmentAnalytics.ready(() => {
            this.segmentAnalytics.reset();
        });
    }
    /**
     * A wrapper for the Segment Analytics.js `track` method. We only accept a
     *   single signature, rather than the overloaded signatures Segment allows.
     *
     * See: https://segment.com/docs/sources/website/analytics.js/#track
     *
     * @param eventDisplayName e.g. "Product Added", as opposed to "productAdded"
     * @param properties A bag of data to send along with this event.
     */
    track(eventDisplayName, properties) {
        const options = {
            anonymousId: this._client.sessionId,
            context: {
                traits: this.deriveAcquisitionAttributes(),
                pageKey: this._client.getPageKey()
            }
        };
        this.segmentAnalytics.ready(() => {
            this.segmentAnalytics.track(eventDisplayName, properties, options);
        });
    }
    /**
     * A wrapper for the Segment Analytics.js `page` method. We only accept a
     *   single signature, rather than the overloaded signatures Segment allows.
     *
     * See: https://segment.com/docs/sources/website/analytics.js/#page
     *
     * @param properties A bag of data to send along with this event.
     */
    page(properties = {}) {
        const options = {
            anonymousId: this._client.sessionId,
            context: {
                traits: this.deriveAcquisitionAttributes(),
                pageKey: this._client.getPageKey()
            }
        };
        this.segmentAnalytics.ready(() => {
            this.segmentAnalytics.page(properties, options);
        });
    }
    /**
     * Mark the start of a new session and associate it with marketing/acquisition data
     *
     * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
     *
     * @param attributes Acquisition attributes associated with this touchpoint.
     */
    touch(attributes) {
        this.track('Touch', attributes);
    }
    /**
     * The acquisition attributes for the tracked user "right now":
     *
     * We send last-touch attribution data with every tracking event, however
     *   last-touch attribution does not make sense for "initAppUrl" so we
     *   use first-touch for that attribute.
     *
     * Attribution data should be derived by our data warehouse rather than being
     *   tracked on the frontend. Sending this data along with individual tracking
     *   events is a bad practice and will be deprecated/removed eventually.
     */
    deriveAcquisitionAttributes() {
        const firstTouchAttributes = this._client.firstTouch ? this._client.firstTouch.attributes : {};
        const lastTouchAttributes = this._client.lastTouch ? this._client.lastTouch.attributes : {};
        return Object.assign(Object.assign({}, lastTouchAttributes), { initAppUrl: firstTouchAttributes.initAppUrl });
    }
}

class FacebookTracker extends Tracker {
    constructor(client, options) {
        super(client);
        this.queuedEvents = [];
        this.initializedPixelIds = new Set();
        if (!window.fbq) {
            throw new Error('The Facebook Pixel snippet is missing, but must be installed to use the FacebookTracker.');
        }
        this.options = options;
        // Initialize the default pixel
        this.initPixel(this.options.pixelIds.default);
    }
    get fbq() {
        return window.fbq;
    }
    /**
     * Initialize a pixel, ensuring it is only initialized once. The exception to this rule
     *   is when a user's traits have changed, in which case we re-initialize the pixel.
     *
     * The Facebook documentation suggests this is bad practice, or an error, but others
     *   say it's supported and is the only way to add traits to a pixel without refreshing
     *   the page (i.e. in a single page app).
     *
     * See: https://stackoverflow.com/questions/39355191/advanced-matching-with-new-facebook-pixel-in-javascript-web-app
     */
    initPixel(pixelId) {
        if (this.initializedPixelIds.has(pixelId)) {
            return;
        }
        this.fbq('init', pixelId);
        this.initializedPixelIds.add(pixelId);
    }
    /**
     * When a user's traits have changed, we must re-initialize the pixel in order to
     *   associate the new traits with that pixel.
     *
     * The Facebook documentation suggests this is bad practice, or an error, but others
     *   say it's supported and is the only way to add traits to a pixel without refreshing
     *   the page (i.e. in a single page app).
     *
     * See: https://stackoverflow.com/questions/39355191/advanced-matching-with-new-facebook-pixel-in-javascript-web-app
     */
    addTraits(traits) {
        const advancedMatchingData = this.getAdvancedMatchingDataFromUserTraits(traits);
        this.initializedPixelIds.forEach(pixelId => {
            // @ts-ignore Facebook Pixel typings are incomplete
            this.fbq('init', pixelId, advancedMatchingData);
        });
    }
    getAdvancedMatchingDataFromUserTraits(traits) {
        return {
            ct: traits.address &&
                traits.address.city &&
                traits.address.city.toLowerCase().replace(/\s/g, ''),
            // TODO: Normalize state to two-letter state code
            st: traits.address && traits.address.state && traits.address.state.toLowerCase(),
            // TODO: Normalize country to two-letter country code
            country: traits.address && traits.address.country,
            zp: traits.address && traits.address.postalCode,
            db: traits.dateOfBirth && dateFns.format(traits.dateOfBirth, 'yyyyMMdd'),
            em: traits.email && traits.email.toLowerCase(),
            fn: traits.firstName && traits.firstName.toLowerCase(),
            ln: traits.lastName && traits.lastName.toLowerCase(),
            ge: traits.gender,
            ph: traits.phone && traits.phone.replace(/[^\d]/g, '')
        };
    }
    /**
     * Remove the queued events when the user logs out
     */
    reset() {
        this.queuedEvents = [];
    }
    /**
     * A wrapper for the Facebook Pixel tracking API (`fbq`).
     *
     * Events are always tracked to a default pixel ID, and are additionally tracked to a
     *   department-specific pixel ID if the user was associated with a department when
     *   they were acquired. If the department is not known at the time the event is fired
     *   those events are queued in case they are associated with a department later on
     *   (at which point all queued events are flushed to the department-specific pixel)
     */
    track(eventName, parameters) {
        // TODO: Track "active" or "acquisition" department
        const departmentSlug = this._client.getDepartment();
        const defaultPixelId = this.options.pixelIds.default;
        const departmentPixelId = departmentSlug && this.options.pixelIds[departmentSlug];
        // @ts-ignore Facebook Pixel typings are incomplete
        this.fbq('trackSingle', defaultPixelId, eventName, parameters);
        if (departmentPixelId) {
            // @ts-ignore Facebook Pixel typings are incomplete
            this.fbq('trackSingle', departmentPixelId, eventName, parameters);
        }
        else {
            this.queuedEvents.push({
                eventName,
                parameters
            });
        }
    }
    /**
     * When the user's department is set for the first time, flush any queued events to
     *   that department's pixel
     */
    setDepartment(departmentSlug) {
        const departmentPixelId = this.options.pixelIds[departmentSlug];
        if (!departmentPixelId) {
            console.warn(`No Facebook Pixel ID configured for ${departmentSlug}`);
        }
        // Initialize the department-specific pixel
        this.initPixel(departmentPixelId);
        let event;
        while ((event = this.queuedEvents.shift())) {
            // @ts-ignore Facebook Pixel typings are incomplete
            window.fbq('trackSingle', departmentPixelId, event.eventName, event.parameters);
        }
    }
    /**
     * Convert our internal tracking representation of a sellable to the format Facebook
     *   expects.
     *
     * See: https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#object-properties
     */
    getFacebookContentsFromSellableTrackingProperties(sellable) {
        return {
            id: sellable.product_id,
            quantity: sellable.quantity || 1,
            item_price: sellable.price || undefined
        };
    }
    pageViewed() {
        this.track('PageView');
    }
    checkoutStarted(properties) {
        const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties);
        const contentIds = contents.map(content => content.id);
        const contentCategory = properties.products.length > 0 ? properties.products[0].category : undefined;
        this.track('InitiateCheckout', {
            content_category: contentCategory,
            content_ids: contentIds,
            // @ts-ignore Facebook Pixel typings are incomplete
            contents,
            currency: properties.currency,
            num_items: contents.length,
            value: parseFloat(properties.value)
        });
    }
    orderCompleted(properties) {
        const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties);
        const contentIds = contents.map(content => content.id);
        this.track('Purchase', {
            content_ids: contentIds,
            // TODO: What should 'content_name' be for a new request?
            // content_name: ,
            content_type: 'product',
            // @ts-ignore Facebook Pixel typings are incomplete
            contents,
            num_items: contents.length,
            value: parseFloat(properties.value),
            currency: properties.currency
        });
    }
    productViewed(properties) {
        const content = this.getFacebookContentsFromSellableTrackingProperties(properties);
        this.track('ViewContent', {
            // TODO: Verify this is correct. If we only track parent sellables we may want to use 'product_group'.
            content_type: 'product',
            // @ts-ignore Facebook Pixel typings are incomplete
            contents: [content],
            content_ids: [content.id],
            content_name: properties.name,
            content_category: properties.category,
            currency: properties.currency,
            // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
            //   or the value of the sellable.
            value: properties.price ? parseFloat(properties.price) : undefined
        });
    }
    productAdded(properties) {
        const content = this.getFacebookContentsFromSellableTrackingProperties(properties);
        this.track('AddToCart', {
            content_type: 'product',
            // @ts-ignore Facebook Pixel typings are incomplete
            contents: [content],
            content_ids: [content.id],
            content_name: properties.name,
            content_category: properties.category,
            currency: properties.currency,
            // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
            //   or the value of the sellable.
            value: properties.price ? parseFloat(properties.price) : undefined
        });
    }
    productAddedToWishlist(properties) {
        const content = this.getFacebookContentsFromSellableTrackingProperties(properties);
        this.track('AddToWishlist', {
            content_type: 'product',
            // @ts-ignore Facebook Pixel typings are incomplete
            contents: [content],
            content_ids: [content.id],
            content_name: properties.name,
            content_category: properties.category,
            currency: properties.currency,
            // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
            //   or the value of the sellable.
            value: properties.price ? parseFloat(properties.price) : undefined
        });
    }
    requestSubmitted(properties) {
        this.track('Lead', {
            content_category: properties.category_slug
            // TODO: What should 'content_name' be for a new request?
            // content_name: '',
            // TODO: User-level currency tracking
            // currency: '',
            // TODO: Use 'value' for LTV tracking?
            // value
        });
    }
    productsSearched(properties) {
        this.track('Search', {
            // TODO: What should 'content_*' represent for search tracking?
            // content_category,
            // content_ids,
            // contents,
            // currency,
            search_string: properties.query.keywords
            // TODO: Assign a value to searches
            // value
        });
    }
    viewCurationLtv(properties) {
        this.track('ViewCurationLTV', {
            value: properties.value,
            currency: properties.currency,
            content_category: properties.content_category
        });
    }
    curatedListViewed(properties) {
        const contents = properties.items.map(this.getFacebookContentsFromSellableTrackingProperties);
        const contentIds = contents.map(content => content.id);
        this.track('ViewContent', {
            content_ids: contentIds,
            // TODO: What should the 'content_name' be for a curated list?
            // content_name: '',
            content_type: 'product',
            // @ts-ignore Facebook Pixel typings are incomplete
            contents: contents,
            currency: properties.currency,
            content_category: properties.content_category || undefined,
            // TODO: Will this artificially inflate the value of the user if we consider all curated items in the "value"?
            value: contents.reduce((sum, item) => {
                // TODO: 'item_price' is incorrectly typed as a string. parseInt can be removed when the typings are fixed.
                return sum + parseInt(item.item_price || '0', 10);
            }, 0)
        });
    }
    paymentInfoEntered(properties) {
        const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties);
        const contentIds = contents.map(content => content.id);
        const contentCategory = properties.products.length > 0 ? properties.products[0].category : undefined;
        this.track('AddPaymentInfo', {
            content_type: 'product',
            content_category: contentCategory,
            content_ids: contentIds,
            // @ts-ignore Facebook Pixel typings are incomplete
            contents: contents,
            currency: properties.currency,
            value: properties.value ? parseFloat(properties.value) : undefined
        });
    }
    expertApplicationSubmitted() {
        this.track('SubmitApplication');
    }
    userRegistered() {
        const contentName = [
            this._client.getTraits().firstName,
            this._client.getTraits().lastName
        ].join(' ');
        // @ts-ignore Facebook Pixel typings are incomplete
        this.track('CompleteRegistration', {
            content_name: contentName,
            // @ts-ignore Facebook Pixel typings are inaccurate
            status: true
            // TODO: User-level currency tracking
            // currency: 'USD',
        });
    }
}

class FullStoryTracker extends EmittingTracker {
    constructor(client, options = { sampleRate: 0.1 }) {
        super(client);
        this._options = options;
        if (!window.FS) {
            throw new Error('The FullStory snippet is missing, but must be installed to use the FullStoryTracker.');
        }
        if (this.shouldRecord()) {
            this.fullstory.restart();
        }
        this.onEvent(this.handleEvent.bind(this));
    }
    /**
     * Resolve window.FS.
     *
     * This getting exists solely to simplify typechecking. We can be certain
     *   `window.FS` is defined since we check for it in the constructor.
     */
    get fullstory() {
        return window.FS;
    }
    /**
     * Should session recording/playback be enabled for the tracked user?
     */
    shouldRecord() {
        if (this._client.identified) {
            return true;
        }
        // Generate a consistent number between 0 and 99 (inclusive)
        const hash = hashIndex(this._client.sessionId, 100);
        return hash < this._options.sampleRate * 100;
    }
    /**
     * Format an object according to FullStory's JSON validation requirements.
     *   In short, FullStory requires every property/key to be suffixed with a
     *   type.
     *
     * See: https://help.fullstory.com/hc/en-us/articles/360020623234-FS-Recording-Client-API-Requirements
     */
    formatCustomProperties(obj) {
        const formatted = {};
        const reserved = new Set(['uid', 'displayName', 'email', 'acctId', 'website']);
        for (let [property, value] of Object.entries(obj)) {
            if (reserved.has(property)) {
                // Reserved properties
                formatted[property] = value;
            }
            else if (isString(value)) {
                // String
                formatted[`${property}_str`] = value;
            }
            else if (isInteger(value)) {
                // Integer
                formatted[`${property}_int`] = value;
            }
            else if (isNumber(value)) {
                // Real
                formatted[`${property}_real`] = value;
            }
            else if (isDate(value)) {
                // Date
                formatted[`${property}_date`] = value;
            }
            else if (isBoolean(value)) {
                // Boolean
                formatted[`${property}_bool`] = value;
            }
            else if (isArray(value)) {
                if (value.every(item => isString(item))) {
                    // String[]
                    formatted[`${property}_strs`] = value;
                }
                else if (value.every(item => isInteger(item))) {
                    // Integer[]
                    formatted[`${property}_ints`] = value;
                }
                else if (value.every(item => isNumber(item))) {
                    // Real[]
                    formatted[`${property}_reals`] = value;
                }
                else if (value.every(item => isDate(item))) {
                    // Date[]
                    formatted[`${property}_reals`] = value;
                }
                else if (value.every(item => isBoolean(item))) {
                    // Boolean[]
                    formatted[`${property}_bools`] = value;
                }
            }
            else if (isPlainObject(value)) {
                // Sub-objects
                formatted[property] = this.formatCustomProperties(value);
            }
        }
        return formatted;
    }
    /**
     * Explicitly turn on recording for this session.
     */
    record() {
        this.fullstory.restart();
    }
    /**
     * Handle events as they are tracked.
     *
     * Segment treats page views as special events, so we forward events to
     *   either SegmentTracker.track or SegmentTracker.page as appropriate.
     */
    handleEvent(eventName, eventData) {
        const eventDisplayName = displayNameForEvent(eventName);
        // We don't particularly care about page view events in FullStory.
        if (eventName !== 'pageViewed') {
            this.track(eventDisplayName, eventData);
        }
    }
    /**
     * Associate a tracked user with a persistent User ID.
     *
     * @param trackingId
     */
    identify(trackingId) {
        this.fullstory.restart();
        this.fullstory.identify(trackingId);
    }
    /**
     * Add traits (PII) to the tracked user.
     */
    addTraits(traits) {
        let fsUserVars = {};
        fsUserVars.displayName = [traits.firstName, traits.lastName].join(' ').trim();
        fsUserVars.email = traits.email;
        fsUserVars.uid = this._client.trackingId;
        const customTraits = omit(traits, ['firstName', 'lastName', 'email', 'address']);
        fsUserVars = Object.assign(fsUserVars, customTraits);
        this.fullstory.setUserVars(this.formatCustomProperties(fsUserVars));
    }
    /**
     * A wrapper for the FullStory `event` method.
     *
     * @param eventDisplayName e.g. "Product Added", as opposed to "productAdded"
     * @param properties A bag of data to send along with this event.
     */
    track(eventDisplayName, properties) {
        this.fullstory.event(eventDisplayName, this.formatCustomProperties(properties || {}));
    }
    /**
     * Mark the start of a new session and associate it with marketing/acquisition data
     *
     * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
     *
     * @param attributes Acquisition attributes associated with this touchpoint.
     */
    touch(attributes) {
        this.track('Touch', attributes);
    }
    /**
     * Disassociate the tracked user from the previously identified user ID, and
     *   clear any other user-specific state/traits.
     */
    reset() {
        // TODO: Can/should we disassociate the previously authenticated session with the
        //   logged-out user's session? Is that even desired?
    }
}

class SmartLookTracker extends EmittingTracker {
    constructor(client, options) {
        super(client);
        this._options = options;
        this._initialized = false;
        if (!window.smartlook) {
            throw new Error('The SmartLook snippet is missing, but must be installed to use the SmartLookTracker.');
        }
        if (this.shouldRecord()) {
            this.record();
        }
        this.onEvent(this.handleEvent.bind(this));
    }
    /**
     * Resolve window.FS.
     *
     * This getting exists solely to simplify typechecking. We can be certain
     *   `window.smartlook` is defined since we check for it in the constructor.
     */
    get smartlook() {
        return window.smartlook;
    }
    /**
     * Should session recording/playback be enabled for the tracked user?
     */
    shouldRecord() {
        if (this._client.identified) {
            return true;
        }
        // Generate a consistent number between 0 and 99 (inclusive)
        const hash = hashIndex(this._client.sessionId, 100);
        return hash < (this._options.sampleRate || 0.1) * 100;
    }
    /**
     * Explicitly turn on recording for this session.
     *
     * HACK: Smartlook's initialization logic isn't very robust. We pause
     *   recording immediately, and then resume recording if the user is
     *   determined to be "high value", or in our sampling population.
     *   Smartlook's SDK does not properly "resume" if you call pause and
     *   then resume before their remote JS has downloaded and initialized.
     *
     * To get around that issue, we repeatedly call "resume" until we detect
     *   that the "ready" callback has fired (this callback functionality is
     *   undocumented, but it works). The callback is not fired if recording
     *   is paused when the SDK loads.
     */
    record() {
        // If the SDK is already initialized, calling "resume" directly is sufficient
        if (this._initialized) {
            this.smartlook('resume');
            return;
        }
        // Otherwise, register a callback to fire when the SDK starts recording.
        this.smartlook(() => {
            this._initialized = true;
        });
        // ... And attempt to record every 250ms
        const interval = setInterval(() => {
            if (this._initialized) {
                clearInterval(interval);
                return;
            }
            // Eventually this will trigger the callback above to fire, and
            //  the next interval will be a no-op.
            this.smartlook('resume');
        }, 250);
        // If the SDK doesn't initialize in 10 seconds, give up.
        setTimeout(() => {
            if (!this._initialized) {
                clearInterval(interval);
            }
        }, 10000);
    }
    /**
     * Handle events as they are tracked.
     *
     * Segment treats page views as special events, so we forward events to
     *   either SegmentTracker.track or SegmentTracker.page as appropriate.
     */
    handleEvent(eventName, eventData) {
        const eventDisplayName = displayNameForEvent(eventName);
        // We don't particularly care about page view events in SmartLook.
        if (eventName !== 'pageViewed') {
            this.track(eventDisplayName, eventData);
        }
    }
    /**
     * Associate a tracked user with a persistent User ID.
     *
     * @param trackingId
     */
    identify(trackingId) {
        this.record();
        this.smartlook('identify', trackingId);
    }
    /**
     * Add traits to the tracked user.
     */
    addTraits(traits) {
        const customTraits = {};
        if (traits.firstName || traits.lastName) {
            customTraits.name = [traits.firstName, traits.lastName].join(' ').trim();
        }
        if (traits.email) {
            customTraits.email = traits.email;
        }
        for (let [key, value] of Object.entries(omit(traits, ['firstName', 'lastName', 'email', 'address']))) {
            if (value) {
                customTraits[key] = value.toString();
            }
        }
        this.smartlook('identify', this._client.trackingId, customTraits);
    }
    /**
     * A wrapper for the SmartLook `track` method.
     *
     * See: https://segment.com/docs/sources/website/analytics.js/#track
     *
     * @param eventDisplayName e.g. "Product Added", as opposed to "productAdded"
     * @param properties A bag of data to send along with this event.
     */
    track(eventDisplayName, properties) {
        this.smartlook('track', eventDisplayName, properties);
    }
    /**
     * Mark the start of a new session and associate it with marketing/acquisition data
     *
     * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
     *
     * @param attributes Acquisition attributes associated with this touchpoint.
     */
    touch(attributes) {
        this.track('Touch', attributes);
    }
    /**
     * Disassociate the tracked user from the previously identified user ID, and
     *   clear any other user-specific state/traits.
     */
    reset() {
        this.smartlook('anonymize');
    }
}

class LiveSessionTracker extends EmittingTracker {
    constructor(client, options = { sampleRate: 0.1 }) {
        super(client);
        this._options = options;
        if (!window.__ls) {
            throw new Error('The LiveSession snippet is missing, but must be installed to use the LiveSessionTracker.');
        }
        if (this.shouldRecord()) {
            this.record();
        }
    }
    /**
     * Resolve window.__ls.
     *
     * This getting exists solely to simplify typechecking. We can be certain
     *   `window.__ls` is defined since we check for it in the constructor.
     */
    get livesession() {
        return window.__ls;
    }
    /**
     * Should session recording/playback be enabled for the tracked user?
     */
    shouldRecord() {
        if (this._client.identified) {
            return true;
        }
        // Generate a consistent number between 0 and 99 (inclusive)
        const hash = hashIndex(this._client.sessionId, 100);
        return hash < this._options.sampleRate * 100;
    }
    /**
     * Explicitly turn on recording for this session.
     */
    record() {
        this.livesession('newPageView');
    }
    /**
     * Associate a tracked user with a persistent User ID.
     *
     * @param trackingId
     */
    identify(_trackingId) {
        this.record();
    }
    /**
     * Add traits (PII) to the tracked user.
     */
    addTraits(traits) {
        const name = [traits.firstName, traits.lastName].join(' ').trim();
        const customTraits = {};
        for (let [key, value] of Object.entries(omit(traits, ['firstName', 'lastName', 'email', 'address']))) {
            if (value) {
                customTraits[key] = value === null || value === void 0 ? void 0 : value.toString();
            }
        }
        this.livesession('identify', {
            name: name,
            email: traits.email,
            params: customTraits
        });
    }
    /**
     * Disassociate the tracked user from the previously identified user ID, and
     *   clear any other user-specific state/traits.
     */
    reset() {
        // TODO: Can/should we disassociate the previously authenticated session with the
        //   logged-out user's session? Is that even desired?
    }
}

function acquisitionAttributesFromQueryString(queryString) {
    const parameters = new URLSearchParams(queryString);
    const attributes = {};
    parameters.forEach((queryParamValue, queryParamKey) => {
        const attributeKey = QUERY_PARAMETERS_TO_ACQUISITION_ATTRIBUTES[queryParamKey];
        if (attributeKey) {
            attributes[attributeKey] = queryParamValue;
        }
    });
    if (!attributes.source) {
        attributes.source = 'organic';
    }
    return attributes;
}
const QUERY_PARAMETERS_TO_ACQUISITION_ATTRIBUTES = {
    ad_id: 'adId',
    ad_type: 'adType',
    ad_search_query: 'searchQuery',
    adgroup_id: 'adgroupId',
    expert_id: 'expertId',
    campaign_id: 'campaignId',
    department: 'department',
    gclid: 'googleClickId',
    fbclid: 'facebookClickId',
    product_id: 'productId',
    referrer_id: 'referrerId',
    target_id: 'targetId',
    utm_adgroup: 'adgroup',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_term: 'term',
    utm_content: 'content',
    utm_source: 'source',
    utm_id: 'campaignId',
    vt_match_type: 'matchType',
    vt_device: 'device',
    vt_device_model: 'deviceModel',
    vt_geo_location: 'geoLocation',
    vt_interest_location: 'interestLocation',
    vt_placement: 'placement',
    vt_target_id: 'targetId',
    vt_ad_position: 'adPosition',
    vt_network: 'network'
};
const ACQUISITION_QUERY_PARAMETERS = Object.keys(QUERY_PARAMETERS_TO_ACQUISITION_ATTRIBUTES);

function acquisitionAttributesFromWindow() {
    return Object.assign({ referrerUrl: window.document.referrer, initAppUrl: window.location.href }, acquisitionAttributesFromQueryString(window.location.search));
}

exports.ACQUISITION_QUERY_PARAMETERS = ACQUISITION_QUERY_PARAMETERS;
exports.Analytics = Analytics;
exports.ConsoleTracker = ConsoleTracker;
exports.EmittingTracker = EmittingTracker;
exports.FacebookTracker = FacebookTracker;
exports.FullStoryTracker = FullStoryTracker;
exports.LiveSessionTracker = LiveSessionTracker;
exports.SegmentTracker = SegmentTracker;
exports.SmartLookTracker = SmartLookTracker;
exports.Tracker = Tracker;
exports.acquisitionAttributesFromQueryString = acquisitionAttributesFromQueryString;
exports.acquisitionAttributesFromWindow = acquisitionAttributesFromWindow;
exports.displayNameForEvent = displayNameForEvent;
//# sourceMappingURL=index.js.map
